import { useState } from 'react'
import { Input, Select } from '../../../shared/components'
import { PriceInput } from '../../../shared/components/PriceInput'
import Button from '../../../shared/components/Button'
import type { Collection, CollectionDiscountType, CollectionPayload } from '../../../features/products/api/metaDataApi'

const DISCOUNT_OPTIONS = [
  { title: 'No discount', value: 'NONE' },
  { title: 'Percentage off', value: 'PERCENTAGE' },
  { title: 'Fixed amount off', value: 'FLAT' },
]

/** `2026-08-14T09:30:00.000Z` -> `2026-08-14T09:30`, the value datetime-local wants. */
const toLocalInput = (iso?: string | null) => (iso ? iso.slice(0, 16) : '')
const toIso = (local: string) => (local ? new Date(local).toISOString() : null)

type DiscountChoice = 'NONE' | CollectionDiscountType

interface Props {
  collection?: Collection
  isLoading: boolean
  onSubmit: (payload: CollectionPayload) => Promise<unknown>
  onCancel: () => void
}

/**
 * Create/edit a collection with its optional collection-level discount (PE-2135).
 *
 * Mirrors product-service's Create/UpdateCollectionDto:
 * - PERCENTAGE `discountValue` is 0-100; FLAT is a kobo amount off basePrice.
 * - Clearing the discount sends explicit nulls, which is how the API removes it.
 */
export const CollectionForm = ({ collection, isLoading, onSubmit, onCancel }: Props) => {
  const [name, setName] = useState(collection?.name ?? '')
  const [choice, setChoice] = useState<DiscountChoice>(collection?.discountType ?? 'NONE')
  const [percent, setPercent] = useState(
    collection?.discountType === 'PERCENTAGE' ? String(collection.discountValue ?? '') : '',
  )
  const [flatKobo, setFlatKobo] = useState(collection?.discountType === 'FLAT' ? (collection.discountValue ?? 0) : 0)
  const [startsAt, setStartsAt] = useState(toLocalInput(collection?.discountStartsAt))
  const [endsAt, setEndsAt] = useState(toLocalInput(collection?.discountEndsAt))
  const [error, setError] = useState<string | null>(null)

  const percentNumber = Number(percent)

  const validate = (): string | null => {
    if (!name.trim()) return 'A collection name is required.'
    if (choice === 'PERCENTAGE') {
      if (!percent.trim() || Number.isNaN(percentNumber)) return 'Enter a percentage between 0 and 100.'
      if (!Number.isInteger(percentNumber)) return 'The percentage must be a whole number.'
      if (percentNumber < 0 || percentNumber > 100) return 'The percentage must be between 0 and 100.'
    }
    if (choice === 'FLAT' && flatKobo <= 0) return 'Enter the amount to take off each product.'
    if (startsAt && endsAt && new Date(endsAt) <= new Date(startsAt)) {
      return 'The end date must be after the start date.'
    }
    return null
  }

  const handleSubmit = async () => {
    const problem = validate()
    setError(problem)
    if (problem) return

    const payload: CollectionPayload = { name: name.trim() }
    if (choice === 'NONE') {
      // Explicit nulls are what remove an existing discount.
      payload.discountType = null
      payload.discountValue = null
      payload.discountStartsAt = null
      payload.discountEndsAt = null
    } else {
      payload.discountType = choice
      payload.discountValue = choice === 'PERCENTAGE' ? percentNumber : flatKobo
      payload.discountStartsAt = toIso(startsAt)
      payload.discountEndsAt = toIso(endsAt)
    }
    await onSubmit(payload)
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Input
          label="Name"
          placeholder="Collection name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="h-[38px]"
        />
        <Select
          label="Discount"
          options={DISCOUNT_OPTIONS}
          value={choice}
          onChange={(v) => setChoice(v as DiscountChoice)}
        />
      </div>

      {choice !== 'NONE' && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {choice === 'PERCENTAGE' ? (
            <Input
              label="Percentage off (%)"
              type="number"
              min={0}
              max={100}
              value={percent}
              onChange={(e) => setPercent(e.target.value)}
              className="h-[38px]"
            />
          ) : (
            <PriceInput label="Amount off" value={flatKobo} onChange={setFlatKobo} className="h-[38px]" />
          )}
          <div>
            <label className="block text-[14px] font-[600] text-foreground mb-1">Starts (optional)</label>
            <input
              type="datetime-local"
              value={startsAt}
              onChange={(e) => setStartsAt(e.target.value)}
              className="w-full px-3 border border-[#D9D9D9] bg-muted text-foreground rounded-[10px] h-[38px] text-[14px] focus:outline-none"
            />
          </div>
          <div>
            <label className="block text-[14px] font-[600] text-foreground mb-1">Ends (optional)</label>
            <input
              type="datetime-local"
              value={endsAt}
              onChange={(e) => setEndsAt(e.target.value)}
              className="w-full px-3 border border-[#D9D9D9] bg-muted text-foreground rounded-[10px] h-[38px] text-[14px] focus:outline-none"
            />
          </div>
        </div>
      )}

      {choice !== 'NONE' && (
        <p className="text-[12px] text-muted-foreground">
          Applies to every product in this collection. Leave the dates empty to start now and run open-ended.
        </p>
      )}

      {error && <p className="text-[12px] text-red-600">{error}</p>}

      <div className="flex gap-3">
        <Button size="sm" onClick={handleSubmit} disabled={isLoading} loading={isLoading} className="h-[38px]">
          {collection ? 'Update' : 'Save'}
        </Button>
        <Button size="sm" variant="outline" onClick={onCancel} className="h-[38px]">
          Cancel
        </Button>
      </div>
    </div>
  )
}
